import { useEffect, useMemo, useState } from "react";
import { FaSearchDollar } from "react-icons/fa";
import { MdClose, MdErrorOutline, MdFilterList, MdOutlineInbox, MdRefresh, MdSearch } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import FindPaymentModal from "../../components/findPaymentModal/index.jsx";
import Header from "../../components/header/index.jsx";
import { Loading } from "../../components/loading";
import { getCmvUsers } from "../../scripts/getCmvUsers";
import { getCompanies } from "../../scripts/getCompanies";
import ClientsCmvList from "./components/clientsCmvList/index.jsx";
import ClientsList from "./components/clientsList";
import UserMenu from "./components/userMenu/index.jsx";
import styles from "./css/clients.module.css";



// TIPOS DE CLIENTE (CADA UM TEM A SUA LISTA, O SEU CONJUNTO DE FILTROS E A SUA BUSCA)
const TYPES = [
  { value: "companies", label: "Empresas", placeholder: "Nome fantasia, razão social ou CPF/CNPJ" },
  { value: "cmv", label: "CMV", placeholder: "Nome, e-mail ou CPF" },
];

// FILTROS DE STATUS: `match` DECIDE SE O CLIENTE ENTRA EM CADA OPÇÃO
const COMPANY_FILTERS = [
  { value: "all", label: "Todas", match: () => true },
  { value: "active", label: "Ativas", match: (item) => item?.payment_control?.status == 1 },
  { value: "inactive", label: "Inativas", match: (item) => item?.payment_control?.status == 0 || !item?.payment_control?.status },
  { value: "waiting", label: "Em espera", match: (item) => item.operating == 0 },
  { value: "trial", label: "Em trial", match: (item) => item?.trial?.[0]?.status == "active" },
  { value: "overdue", label: "Inadimplentes", match: (item) => item?.payment_control?.next_payment && new Date(item.payment_control.next_payment) < new Date() },
  { value: "deleted", label: "Excluídas", match: (item) => item.deleted === true },
];

const CMV_FILTERS = [
  { value: "all", label: "Todos", match: () => true },
  { value: "trial", label: "Em trial", match: (item) => item?.trial?.status },
  { value: "overdue", label: "Inadimplentes", match: (item) => item?.payment_control?.next_payment && new Date(item.payment_control.next_payment) < new Date() },
];

// CAMPOS CONSIDERADOS NA BUSCA DE CADA TIPO
const SEARCH_FIELDS = {
  companies: ["name", "document", "razao_social"],
  cmv: ["name", "document", "email"],
};

// NORMALIZA O TEXTO PARA A BUSCA (SEM ACENTOS E SEM DIFERENCIAR MAIÚSCULAS/MINÚSCULAS)
const normalize = (text) => (text ?? "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();


export default function Clients() {
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);
  const [cmvUsers, setCmvUsers] = useState([]);
  const [userCmvMenu, setUserCmvMenu] = useState(false);
  const [userCmv, setUserCmv] = useState(null);
  const [showFindPaymentModal, setShowFindPaymentModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // FILTROS DA TELA
  const [type, setType] = useState("companies");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    handleGetClients();
  }, []);

  const handleGetClients = async () => {
    try {
      setLoading(true);
      setLoadError(false);

      const [resCompanies, resCmv] = await Promise.all([
        getCompanies(),
        getCmvUsers(),
      ]);

      setCompanies(resCompanies);
      setCmvUsers(resCmv);

    } catch (e) {
      console.error("ERROR - INIT SCREEN:", e);
      setLoadError(true);

    } finally {
      setLoading(false);
    }
  };

  const isCompanies = type == "companies";
  const typeData = TYPES.find((item) => item.value == type);
  const sourceList = isCompanies ? companies : cmvUsers;
  const statusFilters = isCompanies ? COMPANY_FILTERS : CMV_FILTERS;
  const activeStatus = statusFilters.find((filter) => filter.value == statusFilter) ?? statusFilters[0];

  // CLIENTES QUE BATEM COM A BUSCA. OS CONTADORES DOS CHIPS PARTEM DESTA LISTA
  const searched = useMemo(() => {
    const term = normalize(search).trim();

    if (!term) return sourceList;

    return sourceList.filter((item) => SEARCH_FIELDS[type].some((field) => normalize(item?.[field]).includes(term)));
  }, [sourceList, search, type]);

  // LISTA EXIBIDA: SEMPRE DERIVADA DA LISTA COMPLETA, DA BUSCA E DO FILTRO DE STATUS
  const clientsRender = useMemo(
    () => searched.filter((item) => activeStatus.match(item)),
    [searched, activeStatus]
  );

  const hasActiveFilter = statusFilter != "all" || search.trim() != "";

  const clearFilters = () => {
    setStatusFilter("all");
    setSearch("");
  };

  // OS FILTROS DE STATUS SÃO DIFERENTES EM CADA TIPO, ENTÃO TROCAR DE TIPO VOLTA PARA "TODOS"
  const changeType = (value) => {
    setType(value);
    setStatusFilter("all");
  };

  const handleOpenCompany = ({ id }) => {
    navigate(`/clients/${id}`);
  };

  const handleOpenUser = ({ id }) => {
    const usr = cmvUsers.find((user) => user.id == id);
    setUserCmvMenu(true);
    setUserCmv(usr);
  };

  const handleOpenPayer = (payer) => {
    if (payer.type == "company") {
      handleOpenCompany({ id: payer.id });
    } else {
      handleOpenUser({ id: payer.id });
    }
  };

  const singular = isCompanies ? "empresa" : "usuário";
  const plural = isCompanies ? "empresas" : "usuários";

  return (
    <div>
      <Header>
        <h2 className={styles.titleCompaniesArea} style={{ color: "#fff" }}>Clientes</h2>
      </Header>

      <div className={styles.container}>
        <div className={styles.companiesArea}>

          {/* FILTROS */}
          {!loading && !loadError && (
            <div className={styles.filtersArea}>
              <div className={styles.filtersHeader}>
                <h3><MdFilterList size={18} /> Filtrar clientes</h3>

                <div className={styles.filtersActions}>
                  {hasActiveFilter && (
                    <button className={styles.clearFilters} onClick={clearFilters}>Limpar filtros</button>
                  )}

                  <button className={styles.findPaymentButton} onClick={() => setShowFindPaymentModal(true)}>
                    <FaSearchDollar size={16} /> Validar pagamento
                  </button>
                </div>
              </div>

              <div className={styles.searchBar}>
                <MdSearch size={20} className={styles.searchIcon} />

                <input
                  className={styles.searchInput}
                  type="text"
                  value={search}
                  placeholder={typeData.placeholder}
                  onChange={({ target: { value } }) => setSearch(value)}
                />

                {search != "" && (
                  <button className={styles.searchClear} onClick={() => setSearch("")} aria-label="Limpar busca">
                    <MdClose size={18} />
                  </button>
                )}
              </div>

              <div className={styles.filterGroup}>
                <span className={styles.filterLabel}>Tipo</span>

                <div className={styles.chipsRow}>
                  {TYPES.map((item) => (
                    <button
                      key={item.value}
                      className={`${styles.chip} ${type == item.value ? styles.chipActive : ""}`}
                      onClick={() => changeType(item.value)}
                    >
                      {item.label} <span className={styles.chipCount}>{item.value == "companies" ? companies.length : cmvUsers.length}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.filterGroup}>
                <span className={styles.filterLabel}>Status</span>

                <div className={styles.chipsRow}>
                  {statusFilters.map((filter) => (
                    <button
                      key={filter.value}
                      className={`${styles.chip} ${activeStatus.value == filter.value ? styles.chipActive : ""}`}
                      onClick={() => setStatusFilter(filter.value)}
                    >
                      {filter.label} <span className={styles.chipCount}>{searched.filter((item) => filter.match(item)).length}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}


          {/* LISTA */}
          <div className={styles.companiesTableArea}>
            {!loading && !loadError && sourceList.length > 0 && (
              <div className={styles.listHeader}>
                <span className={styles.listCount}>
                  {clientsRender.length == sourceList.length ? `${sourceList.length} ${plural}` : `${clientsRender.length} de ${sourceList.length} ${plural}`}
                </span>
              </div>
            )}

            {loading ? (
              <Loading />

            ) : loadError ? (
              <div className={styles.stateBox}>
                <div className={`${styles.stateIcon} ${styles.bad}`}><MdErrorOutline size={30} /></div>
                <h3>Não foi possível carregar os clientes</h3>
                <p>Verifique a sua conexão de internet ou entre em contato com o TI para averiguar a situação.</p>

                <button className={styles.stateButton} onClick={handleGetClients}>
                  <MdRefresh size={18} /> Tentar novamente
                </button>
              </div>

            ) : sourceList.length == 0 ? (
              <div className={styles.stateBox}>
                <div className={`${styles.stateIcon} ${styles.total}`}><MdOutlineInbox size={30} /></div>
                <h3>Nenhum cliente cadastrado</h3>
                <p>{isCompanies ? "Quando uma empresa parceira se cadastrar no SofChef, ela aparecerá aqui." : "Quando um usuário se cadastrar no SofChef CMV, ele aparecerá aqui."}</p>
              </div>

            ) : clientsRender.length == 0 ? (
              <div className={styles.stateBox}>
                <div className={`${styles.stateIcon} ${styles.total}`}><MdOutlineInbox size={30} /></div>
                <h3>Nenhum{isCompanies ? "a" : ""} {singular} encontrad{isCompanies ? "a" : "o"}</h3>
                <p>Nenhum{isCompanies ? "a" : ""} {singular} corresponde à busca e aos filtros aplicados.</p>

                <button className={styles.stateButton} onClick={clearFilters}>Limpar filtros</button>
              </div>

            ) : isCompanies ? (
              <ClientsList clients={clientsRender} openCompany={({ id }) => handleOpenCompany({ id })} />

            ) : (
              <ClientsCmvList clients={clientsRender} openUser={({ id }) => handleOpenUser({ id })} />
            )}
          </div>
        </div>
      </div>

      <UserMenu
        view={userCmvMenu}
        data={userCmv}
        close={() => {
          setUserCmvMenu(false);
          setUserCmv(null);
        }}
      />

      <FindPaymentModal
        show={showFindPaymentModal}
        onClose={() => setShowFindPaymentModal(false)}
        onFind={(data) => handleOpenPayer(data)}
      />
    </div>
  );
}
